import axios from 'axios';
import { merge } from 'lodash';
import { parse as parseQS } from 'qs';
import { Api } from '../config/api';
import {
  DEFAULT_CHATBOT_CONFIG_FULLSCREEN,
  DEFAULT_CHATBOT_CONFIG_GREETING,
} from '../config/chatbot';
import type { Config, Metadata } from '../types/configs';
import { getUserId } from './localStorageService';

const getBotConfigFromBackend = async (
  configId?: string,
  parentHost?: string,
  parentHref?: string,
  chatBotUserId?: string,
) => {
  const centralApi = axios.create({ baseURL: Api.CONVERSED_SERVER });

  // * If configId is not specified then send back null
  if (!configId) {
    return null;
  }

  try {
    const configResponse = await centralApi.get(
      `/integrations/webclient/configs/${configId}`,
      {
        headers: {
          'user-company': 'portal',
          'parent-host': parentHost,
          'parent-href': parentHref,
          'init-chat-bot-user-id': chatBotUserId,
        },
      },
    );

    return configResponse.data;
  } catch (_e) {
    return null;
  }
};

const getConfigFromMetadata = (metadata: Metadata) => {
  const { search } = metadata;

  const params = parseQS(search);
  const { refParam } = params;

  if (refParam) {
    return { options: { refParam } };
  }

  return { options: {} };
};

export const getConfigToUse = async (params: {
  metadata: Metadata;
  configFromScript?: Config;
  configId: string;
}): Promise<Config | null> => {
  const { metadata, configFromScript, configId } = params;
  const chatBotUserId = getUserId(configId);

  const botConfigFromBackend = await getBotConfigFromBackend(
    configId,
    metadata.host,
    metadata.href,
    chatBotUserId,
  );

  // * If bot backend fails to send config and script lacks options or scheme then do not load
  if (
    !botConfigFromBackend &&
    (!configFromScript?.options || !configFromScript?.scheme)
  ) {
    return null;
  }

  const configFromMetadata = getConfigFromMetadata(metadata);

  const scriptAndMetadataConfig = merge(configFromScript, configFromMetadata);
  const botBackendMergedConfig = merge(
    { ...botConfigFromBackend, configId: configId || 'default' },
    scriptAndMetadataConfig,
  );
  return merge(DEFAULT_CHATBOT_CONFIG_GREETING, botBackendMergedConfig);
};

// * Requires that object be stringify with qs
export const getConfigToUseFromQueryString = (): Config => {
  const parsedObject = parseQS(window.location.search.substring(1));
  return merge(DEFAULT_CHATBOT_CONFIG_FULLSCREEN, parsedObject);
};
